import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, XCircle, CreditCard, Lightbulb, Building2 } from 'lucide-react';
import { formatCurrency } from '@/lib/types/job';
import type { PaymentErrorDetails } from '@/lib/types/paymentErrors';
import { usePaymentProcessingEnabled } from '@/hooks/usePaymentProcessingEnabled';

interface PaymentProcessingModalProps {
  open: boolean;
  amount: number;
  status: 'processing' | 'success' | 'error';
  errorMessage?: string;
  errorDetails?: PaymentErrorDetails;
  transactionId?: string;
  onClose: () => void;
  onRetry?: () => void;
  onUpdatePaymentMethod?: () => void;
}

export function PaymentProcessingModal({
  open,
  amount,
  status,
  errorMessage,
  errorDetails,
  transactionId,
  onClose,
  onRetry,
  onUpdatePaymentMethod,
}: PaymentProcessingModalProps) {
  const { paymentProcessingEnabled, isLoading: flagLoading } = usePaymentProcessingEnabled();

  if (flagLoading || !paymentProcessingEnabled) return null;

  // Determine which recommendation/action to show based on errorDetails
  const showAchSuggestion = errorDetails?.suggestAch;
  const showUpdatePaymentButton = errorDetails?.suggestUpdatePayment && onUpdatePaymentMethod;
  const canRetry = errorDetails?.canRetry !== false; // Default to true if not specified
  
  return (
    <Dialog open={open} onOpenChange={status !== 'processing' ? onClose : undefined}>
      <DialogContent className="sm:max-w-md">
        {status !== 'processing' && (
          <button
            onClick={onClose}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
          >
            <XCircle className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        )}
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {status === 'processing' && (
              <>
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                Processing Payment
              </>
            )}
            {status === 'success' && (
              <>
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                Payment Successful
              </>
            )}
            {status === 'error' && (
              <>
                <XCircle className="h-5 w-5 text-destructive" />
                Payment Failed
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {status === 'processing' && (
            <>
              <div className="text-center">
                <div className="text-3xl font-bold text-foreground mb-2">
                  {formatCurrency(amount)}
                </div>
                <DialogDescription className="text-muted-foreground">
                  Please wait while we process your payment. This may take a few moments.
                </DialogDescription>
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="rounded-lg bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 p-4 text-center">
                <div className="text-2xl font-bold text-green-900 dark:text-green-100 mb-2">
                  {formatCurrency(amount)}
                </div>
                <p className="text-sm text-green-800 dark:text-green-200">
                  Payment processed successfully!
                </p>
                {transactionId && (
                  <p className="text-xs text-green-700 dark:text-green-300 mt-2">
                    Transaction ID: {transactionId.slice(0, 8)}...
                  </p>
                )}
              </div>
              <DialogDescription className="text-center text-muted-foreground">
                Your purchase order is being created. You'll be redirected shortly.
              </DialogDescription>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 p-4 space-y-3">
                <div className="text-sm font-medium text-red-900 dark:text-red-100">
                  {errorMessage || 'An unexpected error occurred while processing your payment.'}
                </div>
                
                {errorDetails?.recommendation && (
                  <div className="flex items-start gap-2 text-sm text-red-800 dark:text-red-200">
                    <Lightbulb className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{errorDetails.recommendation}</span>
                  </div>
                )}
              </div>
              
              {showAchSuggestion && (
                <div className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 p-3 rounded-lg">
                  <Building2 className="h-4 w-4 shrink-0" />
                  <span>
                    Consider using <strong>ACH bank transfer</strong> for large payments — no card limits and lower fees.
                  </span>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                {showUpdatePaymentButton && (
                  <Button variant="outline" onClick={onUpdatePaymentMethod}>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Update Payment Method
                  </Button>
                )}
                {canRetry && onRetry && (
                  <Button variant="default" onClick={onRetry}>
                    Retry Payment
                  </Button>
                )}
                <Button variant="outline" onClick={onClose}>
                  Close
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
